import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ExternalLink, Search, Sparkles } from 'lucide-react';
import generatedDreamPosts from './posts/pages';
import {
    BlogContainer,
    BlogHero,
    BlogHeroActions,
    BlogHeroEyebrow,
    BlogMetaText,
    BlogToolbar,
    CategoryButton,
    CategoryFilter,
    EmptyState,
    MainTitle,
    NumberBadge,
    NumberBadgeList,
    PostCard,
    PostCardFooter,
    PostDate,
    PostExcerpt,
    PostGrid,
    PostListTitle,
    SearchInput,
    SearchInputWrap,
    SubTitle,
    TagList,
    TagPill
} from './BlogStyles';

const CATEGORY_ORDER = [
    '전체',
    '로또 번호 만들기',
    '뱀/살모사 꿈',
    '용 꿈',
    '돼지 꿈',
    '호랑이 꿈',
    '사과 꿈',
    '바나나 꿈',
    '야구 꿈',
    '축구 꿈',
    '조규성 꿈',
    '책 꿈',
    '과일 꿈',
    '스포츠 꿈',
    '인물 꿈',
    '공부 꿈',
    '태몽',
    '로또 꿈',
    '재물운 꿈',
    '관계운 꿈',
    '경고 꿈'
];

const mapApiPost = (post) => ({
    id: post.ID || post.id,
    title: post.TITLE || post.title,
    excerpt: post.EXCERPT || post.excerpt,
    slug: post.SLUG || post.slug,
    createdAt: post.CREATED_AT || post.createdAt || post.publishedAt,
    updatedAt: post.UPDATED_AT || post.updatedAt,
    category: post.CATEGORY || post.category || '꿈해몽',
    tags: post.TAGS || post.tags || ['꿈해몽'],
    seo: {
        title: post.SEO_TITLE || post.seo?.title,
        description: post.SEO_DESCRIPTION || post.seo?.description,
        canonicalUrl: post.SEO_CANONICAL_URL || post.seo?.canonicalUrl
    }
});

const mergePosts = (apiPosts) => {
    const postsBySlug = new Map();

    apiPosts
        .filter((post) => post.slug)
        .forEach((post) => postsBySlug.set(post.slug, post));

    generatedDreamPosts.forEach((post) => {
        if (!postsBySlug.has(post.slug)) {
            postsBySlug.set(post.slug, post);
        }
    });

    return [...postsBySlug.values()].sort((a, b) => {
        const dateA = new Date(a.publishedAt || a.createdAt || a.updatedAt || 0).getTime();
        const dateB = new Date(b.publishedAt || b.createdAt || b.updatedAt || 0).getTime();
        return dateB - dateA;
    });
};

const postMatchesCategory = (post, category) => {
    if (category === '전체') return true;
    return post.category === category || post.tags?.includes(category);
};

const formatDate = (dateString) => {
    if (!dateString) return '날짜 없음';

    try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
            return '날짜 형식 오류';
        }

        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch (error) {
        return '날짜 오류';
    }
};

const BlogMain = () => {
    const [posts, setPosts] = useState(() => mergePosts([]));
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('전체');

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/dream/posts`);
                const mappedPosts = response.data.map(mapApiPost);
                setPosts(mergePosts(mappedPosts));
            } catch (error) {
                console.error('Failed to fetch posts:', error);
                setPosts(mergePosts([]));
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, []);

    const categories = useMemo(() => {
        const availableCategories = new Set();

        posts.forEach((post) => {
            if (post.category) availableCategories.add(post.category);
            post.tags?.forEach((tag) => availableCategories.add(tag));
        });

        return CATEGORY_ORDER.filter((category) => category === '전체' || availableCategories.has(category));
    }, [posts]);

    const filteredPosts = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();

        return posts.filter((post) => {
            const matchesQuery = !query || [
                post.title,
                post.excerpt,
                post.keyword,
                post.category,
                ...(post.tags || [])
            ].join(' ').toLowerCase().includes(query);

            return matchesQuery && postMatchesCategory(post, activeCategory);
        });
    }, [activeCategory, posts, searchTerm]);

    return (
        <>
            <Helmet>
                <title>몽픽 꿈해몽 블로그 - 꿈 키워드별 로또번호와 해석 | 몽픽(MongPick)</title>
                <meta name="description" content="동물, 뱀, 살모사, 용, 돼지, 호랑이 꿈의 의미와 참고용 로또번호를 키워드별로 확인해보세요." />
                <meta name="keywords" content="몽픽, 꿈해몽, 꿈 해석, 로또번호, 살모사 꿈, 용 꿈, 돼지 꿈, 호랑이 꿈" />
                <link rel="canonical" href="https://mongpick.com/blog" />
                <meta property="og:title" content="몽픽 꿈해몽 블로그" />
                <meta property="og:description" content="어젯밤 꾼 꿈의 의미와 행운의 번호를 키워드별로 확인해보세요." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://mongpick.com/blog" />
            </Helmet>

            <BlogHero>
                <BlogHeroEyebrow>꿈해몽 사전</BlogHeroEyebrow>
                <MainTitle>몽픽 꿈해몽 블로그</MainTitle>
                <BlogMetaText>
                    어젯밤 꾼 꿈의 의미와 행운의 번호를 키워드별로 빠르게 확인해보세요.
                </BlogMetaText>
                <BlogHeroActions>
                    <a href="https://mongpick.com/" aria-label="몽픽에서 내 꿈 직접 해석하기">
                        <ExternalLink size={18} />
                        내 꿈 직접 해석하기
                    </a>
                    <Link to="/lotto" aria-label="몽픽 로또 번호 생성기로 이동">
                        <Sparkles size={18} />
                        로또 번호 생성기
                    </Link>
                </BlogHeroActions>
            </BlogHero>

            <BlogContainer>
                <SubTitle>꿈 키워드 검색</SubTitle>
                <BlogToolbar>
                    <SearchInputWrap>
                        <Search size={20} aria-hidden="true" />
                        <SearchInput
                            type="search"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder="꿈 키워드 검색"
                            aria-label="꿈 키워드 검색"
                        />
                    </SearchInputWrap>
                    <CategoryFilter aria-label="꿈해몽 카테고리">
                        {categories.map((category) => (
                            <CategoryButton
                                key={category}
                                type="button"
                                $active={activeCategory === category}
                                onClick={() => setActiveCategory(category)}
                            >
                                {category}
                            </CategoryButton>
                        ))}
                    </CategoryFilter>
                </BlogToolbar>

                {loading && <BlogMetaText>블로그 글을 불러오는 중입니다.</BlogMetaText>}

                {!loading && filteredPosts.length === 0 ? (
                    <EmptyState>
                        <p>찾는 꿈해몽 글이 아직 없다면 몽픽에서 꿈 내용을 직접 입력해보세요.</p>
                        <a href="https://mongpick.com/" aria-label="몽픽에서 꿈 내용 직접 입력하기">
                            <ExternalLink size={18} />
                            몽픽에서 해석하기
                        </a>
                    </EmptyState>
                ) : (
                    <PostGrid>
                        {filteredPosts.map((post) => (
                            <PostCard key={post.id || post.slug} to={`/blog/${post.slug}`}>
                                <TagList>
                                    {(post.tags || [post.category]).slice(0, 3).map((tag) => (
                                        <TagPill key={tag}>{tag}</TagPill>
                                    ))}
                                </TagList>
                                <PostListTitle>{post.title || '제목 없음'}</PostListTitle>
                                <PostExcerpt>{post.excerpt || '요약 없음'}</PostExcerpt>
                                <PostCardFooter>
                                    <PostDate>{formatDate(post.publishedAt || post.createdAt)}</PostDate>
                                    {post.recommendedNumbers?.length ? (
                                        <NumberBadgeList aria-label="추천 번호">
                                            {post.recommendedNumbers.slice(0, 4).map((number) => (
                                                <NumberBadge key={number}>{number}</NumberBadge>
                                            ))}
                                        </NumberBadgeList>
                                    ) : (
                                        <TagPill>추천번호 포함</TagPill>
                                    )}
                                </PostCardFooter>
                            </PostCard>
                        ))}
                    </PostGrid>
                )}
            </BlogContainer>
        </>
    );
};

export default BlogMain;
